import React from "react"
import { graphql } from "gatsby"
import { Helmet } from "react-helmet"
import TypesenseSearchPage, { BUDGET_PRESETS_HOLIDAY } from "../../components/typesense/TypesenseSearchPage"
import {
  HolidayDestinations,
  HolidayEssentials,
  HOLIDAY_FAQ,
} from "../../components/typesense/HolidayEditorial"

const SITE_URL = "https://gadait-international.com"
const CANONICAL = `${SITE_URL}/en/holiday-rentals/`

// Chiffres relevés dans l'index le 04/09/2026 — garder cohérents avec
// HolidayEditorial (source unique de la FAQ) à chaque rafraîchissement.
const HOLIDAY_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}#organization`,
      name: "GADAIT International",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/gadait-icon.png` },
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}#website`,
      url: SITE_URL,
      name: "GADAIT International",
    },
    {
      "@type": "CollectionPage",
      "@id": `${CANONICAL}#collection`,
      url: CANONICAL,
      name: "Luxury Holiday Rentals | GADAIT International",
      description:
        "393 luxury holiday rentals (September 2026): chalets in the French Alps (Courchevel, Megève, Méribel, Saint-Martin-de-Belleville), private villas in Greece (Paros, Porto Heli), Mauritius and the Seychelles. From €500 a night, booked with a dedicated advisor.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${SITE_URL}#website` },
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL}#destinations`,
      name: "Holiday destinations",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Chalet rental in Courchevel — 166 properties",
          url: `${SITE_URL}/en/chalet-rental-courchevel/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Villa rental in Paros — 43 villas",
          url: `${SITE_URL}/en/villa-rental-paros/`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Villa rental in Porto Heli — 27 villas",
          url: `${SITE_URL}/en/villa-rental-porto-heli/`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "Chalet rental in Megève",
          url: `${SITE_URL}/en/chalet-rental-megeve/`,
        },
        {
          "@type": "ListItem",
          position: 5,
          name: "Chalet rental in Méribel",
          url: `${SITE_URL}/en/chalet-rental-meribel/`,
        },
        {
          "@type": "ListItem",
          position: 6,
          name: "Chalet rental in Saint-Martin-de-Belleville",
          url: `${SITE_URL}/en/chalet-rental-saint-martin-de-belleville/`,
        },
      ],
    },
    {
      "@type": "FAQPage",
      "@id": `${CANONICAL}#faq`,
      mainEntity: HOLIDAY_FAQ.en.map(item => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/en` },
        { "@type": "ListItem", position: 2, name: "Holiday Rentals", item: CANONICAL },
      ],
    },
  ],
}

const Page = ({ data }) => (
  <>
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(HOLIDAY_GRAPH)}</script>
      <meta property="og:type" content="website" />
      <meta property="og:url" content={CANONICAL} />
      <meta property="og:title" content="Luxury Holiday Rentals | GADAIT International" />
      <meta
        property="og:description"
        content="Chalets in Courchevel, villas in Paros and Porto Heli, private homes by the Indian Ocean — every stay confirmed by a dedicated advisor."
      />
      <meta property="og:image" content={`${SITE_URL}/search/hero-holiday-poster-1280.jpg`} />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
    <TypesenseSearchPage
      locale="en"
      lockedFilterClauses={["property_status_original_id:=`110023780`"]}
      hiddenFilters={["property_status_name"]}
      budgetPresets={BUDGET_PRESETS_HOLIDAY.en}
      preCollectionSlot={<HolidayDestinations locale="en" />}
      postCollectionSlot={<HolidayEssentials locale="en" />}
      content={{
        canonicalPath: "/en/holiday-rentals/",
        enPath: "/en/holiday-rentals/",
        frPath: "/fr/locations-vacances/",
        eyebrow: "Holiday rentals",
        title: "Luxury holiday rentals",
        intro:
          "Chalets in Courchevel, villas in Paros and Porto Heli, private homes by the Indian Ocean — every stay confirmed by a dedicated advisor.",
        naturalSearchHint:
          'Try: "chalet in Courchevel for New Year, 10 guests"',
        summaryTitle: "Available holiday rentals",
        collectionLabel: "Holiday rentals",
        collectionIntro:
          "Refine holiday rentals by destination, budget and lifestyle.",
        quickSearches: [
          "Chalet in Courchevel",
          "Chalet in Megève",
          "Villa in Paros",
          "Villa in Porto Heli",
          "Villa in Mauritius",
        ],
        searchPlaceholder: data?.search?.searchPlaceholder,
      }}
    />
  </>
)

export default Page

export const query = graphql`
  query HolidayRentalsEnQuery {
    search: datoCmsSearch(locale: { eq: "en" }) {
      ...Search
    }
  }
`
